import { Box, Dialog, IconButton, Stack, Typography } from "@mui/material";
import type { StrategyActivation } from "src/context/api/bidStratTesting/api";
import { BACKDROP_SX, COLORS, Icon, StateMessage } from "./components";
import { ParametersTable, parametersTables, strategyNotFound } from "./utils";
import { tr } from "./i18n";

const CELL_SX = {
    px: 1.5,
    fontSize: 14,
    fontWeight: 500,
    color: COLORS.text,
    borderBottom: `1px solid ${COLORS.border}`,
    whiteSpace: "nowrap",
} as const;

function ParametersGrid({ table }: { table: ParametersTable }) {
    return (
        <Box
            sx={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: "12px",
                overflow: "auto",
                maxHeight: 240,
            }}
        >
            <Box component="table" sx={{ width: 1, borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        {table.columns.map((column) => (
                            <Box
                                component="th"
                                key={column.key}
                                sx={{
                                    ...CELL_SX,
                                    height: 36,
                                    fontWeight: 600,
                                    color: COLORS.secondary,
                                    textAlign: "left",
                                    bgcolor: "#FCFCFD",
                                    borderRight: `1px solid ${COLORS.border}`,
                                    "&:last-of-type": { borderRight: "none" },
                                    position: "sticky",
                                    top: 0,
                                }}
                            >
                                {column.label}
                            </Box>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {table.rows.map((row) => (
                        <tr key={row.id}>
                            {table.columns.map((column) => (
                                <Box
                                    component="td"
                                    key={column.key}
                                    sx={{
                                        ...CELL_SX,
                                        height: 44,
                                        textAlign: column.numeric ? "right" : "left",
                                        fontVariantNumeric: "lining-nums tabular-nums",
                                        borderRight: `1px solid ${COLORS.border}`,
                                        "&:last-of-type": { borderRight: "none" },
                                    }}
                                >
                                    {row.cells[column.key]}
                                </Box>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    );
}

/** "View" of the Parameters column (Figma "Before parameters - all strategy modals"). */
export default function StrategyParametersDialog({
    open,
    strategy,
    campaignName,
    onClose,
}: {
    open: boolean;
    strategy: StrategyActivation[] | null;
    // The Google Ads strategy (G-READ) has no campaign name of its own
    campaignName?: string;
    onClose: () => void;
}) {
    const tables = parametersTables(strategy, campaignName);
    // Several strategies (e.g. Active Mode and KO Bidding Auto lines): one table each, titled
    const several = tables.length > 1;
    return (
        <Dialog
            slotProps={{ backdrop: { sx: BACKDROP_SX } }}
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{ sx: { width: 800, borderRadius: "16px", boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.3)", m: 2 } }}
        >
            <Stack
                direction="row"
                alignItems="center"
                sx={{ px: 3, py: 2.5, bgcolor: "rgba(226, 229, 233, 0.16)", borderBottom: `1px solid ${COLORS.border}` }}
            >
                <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 500, lineHeight: 1, color: COLORS.text }}>
                    {tables.length === 1 ? tables[0].title : tr("parameters.title_several")}
                </Typography>
                <IconButton aria-label={tr("common.close")} onClick={onClose} size="small" sx={{ p: 0.5 }}>
                    <Icon name="x-close" size={16} />
                </IconButton>
            </Stack>
            <Stack gap={2.5} sx={{ p: 3 }}>
                {!tables.length ? (
                    <Box py={3}>
                        <StateMessage title={strategyNotFound()} />
                    </Box>
                ) : (
                    tables.map((table) => (
                        <Stack key={table.title} gap={1}>
                            {several && (
                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{table.title}</Typography>
                            )}
                            <ParametersGrid table={table} />
                        </Stack>
                    ))
                )}
            </Stack>
        </Dialog>
    );
}
